/**
 * The frame every project page shares: the project's name, the certified state
 * it is at, how healthy that reading is, and the tabs that address the rest.
 *
 * One project is one authority today. The frame reads the same snapshot the
 * tab body reads, so the header and the body cannot disagree about which state
 * the project is in.
 */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html } from 'hydrogen/runtime';

import type { SurfaceSnapshot } from '../generated/surface.js';
import { LinkTabs } from './kit.js';
import type { LensId } from './lenses/catalog.js';
import { PROJECT_TABS, projectHref, type ProjectTab } from './nav.js';
import { Absent, authorityState, authorityStateLabel, count, Digest } from './product.js';
import type { SurfaceConnectionState } from './surface-connection.js';

/**
 * The counts a tab carries, read from authority totals rather than from the
 * served window: `counts.changes` is every stored change, and a tab label
 * counting the window would shrink the moment the authority paged.
 */
function tabCount(snapshot: SurfaceSnapshot | null, tab: ProjectTab): number | undefined {
  if (!snapshot) return undefined;
  if (tab === 'changes') return snapshot.counts.changes;
  if (tab === 'work') return snapshot.counts.unique_producers;
  return undefined;
}

/**
 * The certified state the project is at, which is a set: a Frontier with two
 * Heads has no single current state, and electing one would be a claim the
 * authority did not make.
 */
function CertifiedState(snapshot: SurfaceSnapshot | null): TemplateInstance {
  if (!snapshot) return html``;
  const heads = snapshot.canonical.frontier;
  const only = heads.length === 1 ? heads[0] : undefined;
  if (only) {
    return html`<span class="mx-project__state-root">Certified state ${Digest(only.state_root, 12)}</span>`;
  }
  if (heads.length === 0) {
    return html`<span class="mx-project__state-root">Certified state ${Absent('No state has been certified in this project yet')}</span>`;
  }
  return html`<span class="mx-project__state-root">${count(heads.length)} certified heads</span>`;
}

export function ProjectFrame(options: {
  project: string;
  tab: ProjectTab;
  snapshot: SurfaceSnapshot | null;
  connection: SurfaceConnectionState;
  body: TemplateInstance;
  actions?: TemplateInstance;
  /** The body draws its own page title (a change, the Code tab); the project name is then not the h1. */
  bodyTitled?: boolean;
}): TemplateInstance {
  const { project, tab, snapshot } = options;
  const state = authorityState(snapshot, options.connection);
  const tabs = PROJECT_TABS.map((item) => {
    const total = tabCount(snapshot, item.tab);
    const href = projectHref(item.tab, {}, project);
    return total === undefined ? { id: item.tab, label: item.label, href } : { id: item.tab, label: item.label, href, count: total };
  });
  return html`
    <section class="mx-page mx-project" data-region="project" data-project-tab="${tab}">
      <header class="mx-project__head">
        <div class="mx-project__identity">
          ${options.bodyTitled === true
            ? html`<p class="mx-project__name">${project}</p>`
            : html`<h1 class="mx-project__name">${project}</h1>`}
          <span class="mx-project__health" data-state="${state}"><i aria-hidden="true"></i>${authorityStateLabel(state)}</span>
          ${CertifiedState(snapshot)}
        </div>
        ${options.actions ?? html``}
      </header>
      ${LinkTabs({ label: 'Project', tabs, active: tab })}
      <div class="mx-project__body">${options.body}</div>
    </section>
  `;
}

export type ViewOption<Id extends string> = { id: Id; label: string; href: string };

/**
 * A segmented control whose segments are addresses. The lit segment is the
 * address on screen, so it carries `aria-current="page"` as well as the class.
 */
export function ViewSwitch<Id extends string>(options: {
  label: string;
  views: readonly ViewOption<Id>[];
  active: Id;
}): TemplateInstance {
  return LinkTabs({ label: options.label, tabs: options.views, active: options.active });
}

/** The element the shell mounts lens `id` into. */
export function LensHost(id: LensId): TemplateInstance {
  return html`<div class="mx-lens-host" data-lens-host data-lens-id="${id}"></div>`;
}
